import { ChangeDetectionStrategy, Component, Injector, ViewEncapsulation, inject } from '@angular/core';
import { OtherSettingsService } from '../other-settings.service';
import { OtherSettingsType } from '@fi-app/other-settings/other-settings-type';
import { DataFieldService } from '@fi-app/data-field/data-field.service';
import { CoreParameterModel } from '@fi-shared/models';
import { IOtherSettings, IOtherSettingsListItem } from '@fi-app/other-settings/other-settings';
import { AccessRightType, ApplicationSettingsType, ObjectTypes } from '@fi-shared/types';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';
import { PkService } from '@fi-app/external-platform-integration/pk/pk.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { StateAccessCheckService } from '@fi-shared/fi-shared-services/state-access-check.service';
import { AgreementService } from '@fi-app/agreement/agreement.service';
import { BalooService } from '@fi-app/external-platform-integration/baloo/baloo.service';
import { FiRemoteStateFullDataSource } from '@fi-shared/fi-items-list/fi-remote-state-full-data-source';
import { ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';
import { IItemsListDataFetchResult } from '@fi-shared/fi-items-list/fi-items-list';
import { UserService } from '@fi-app/user/user.service';
import { OtherSettingsHelperService } from '@fi-app/other-settings/other-settings-helper.service';
import { comparator } from '@fi-shared/utils/array-sort-comparators';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { PlandayService } from '@fi-app/external-platform-integration/planday/planday.service';
import { FiLoginReminderService } from '@fi-app/other-settings/fi-login-reminder/fi-login-reminder.service';

@Component({
    selector: 'fi-other-settings-list',
    templateUrl: 'other-settings-list.component.html',
    styleUrls: ['./other-settings-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-other-settings-list' },
    standalone: false,
})
export class OtherSettingsListComponent extends FiRemoteStateFullDataSource<IOtherSettings> {
    public defaultSortColumnName = 'Name';
    public itemsListType = FiItemsListType.OtherSettingsList;
    public defaultSortDirection = false;
    public objectType = ObjectTypes.OtherSettings;

    private otherSettingsService = inject(OtherSettingsService);
    private dataFieldService = inject(DataFieldService);
    private accessToApplicationTypeService = inject(AccessToApplicationTypeService);
    private fiLoginReminderService = inject(FiLoginReminderService);
    private pkService = inject(PkService);
    private balooService = inject(BalooService);
    private pladayService = inject(PlandayService);
    private stateAccessCheckService = inject(StateAccessCheckService);
    private agreementService = inject(AgreementService);
    private userService = inject(UserService);
    private otherSettingsHelperService = inject(OtherSettingsHelperService);

    constructor(injector: Injector) {
        super(injector);
    }

    protected getOtherSettingsStateSref(itemType: OtherSettingsType): string {
        return this.otherSettingsHelperService.getOtherSettingsStateSref(itemType);
    }

    protected getOtherSettingsStateSrefParams(
        itemType: OtherSettingsType,
        targetId: number,
    ): Record<string, number | string> {
        return this.otherSettingsHelperService.getOtherSettingsStateSrefParams(itemType, targetId);
    }

    protected openUserViewDialog($event: MouseEvent, userId: number): void {
        $event.stopPropagation();
        this.userService.openUserViewDialog(userId);
    }

    public loadListDataFn(loadArgs: ILoadDataArgs): Promise<IItemsListDataFetchResult<IOtherSettingsListItem>> {
        return new Promise((resolve, reject) => {
            const promises: Promise<void>[] = [];

            const otherSettings: IOtherSettings[] = [];

            if (
                this.accessToApplicationTypeService.HasAccessToApplicationType(
                    ApplicationSettingsType.CoreGlobal,
                    AccessRightType.Admin,
                )
            ) {
                promises.push(
                    this.otherSettingsService.getOtherSettings().then((result) => {
                        otherSettings.push(...result);
                    }),
                );

                promises.push(
                    this.dataFieldService.getDataFieldGroups(new CoreParameterModel()).then((dataFieldGroups) => {
                        const mappedData: IOtherSettingsListItem[] = dataFieldGroups.map((dataFieldGroup) => ({
                            Id: undefined,
                            Type: (() => {
                                if (dataFieldGroup.ObjectType === ObjectTypes.BusinessUnit)
                                    return OtherSettingsType.DataFieldBusinessUnit;
                                if (dataFieldGroup.ObjectType === ObjectTypes.User)
                                    return OtherSettingsType.DataFieldUser;
                            })(),
                            ChangedByUserId: dataFieldGroup.ChangedByUserId,
                            ChangedByUserName: dataFieldGroup.ChangedByUserName,
                            ChangedOn: dataFieldGroup.ChangedOn,
                        }));
                        otherSettings.push(...mappedData);
                    }),
                );
            }

            if (
                this.accessToApplicationTypeService.HasAccessToApplicationType(
                    ApplicationSettingsType.Appearance,
                    AccessRightType.Admin,
                )
            ) {
                const siteDesignData: IOtherSettingsListItem = {
                    Id: undefined,
                    Type: OtherSettingsType.SiteDesign,
                };
                otherSettings.push(siteDesignData);
            }

            if (
                this.accessToApplicationTypeService.HasAccessToApplicationType(
                    ApplicationSettingsType.BillingLocal,
                    AccessRightType.Admin,
                )
            ) {
                const billingData: IOtherSettingsListItem = {
                    Id: undefined,
                    Type: OtherSettingsType.Billing,
                };
                otherSettings.push(billingData);
            }

            if (
                this.accessToApplicationTypeService.HasAccessToApplicationType(
                    ApplicationSettingsType.LoginReminder,
                    AccessRightType.Admin,
                )
            ) {
                promises.push(
                    this.fiLoginReminderService.getLoginReminder().then((result) => {
                        const loginReminderData: IOtherSettingsListItem = {
                            Id: undefined,
                            Type: OtherSettingsType.LoginReminder,
                            ChangedByUserId: result.ChangedByUserId,
                            ChangedByUserName: result.ChangedByUserName,
                            ChangedOn: result.ChangedOn,
                        };
                        otherSettings.push(loginReminderData);
                    }),
                );
            }

            if (
                this.accessToApplicationTypeService.HasAccessToApplicationType(
                    ApplicationSettingsType.Personalkollen,
                    AccessRightType.Admin,
                )
            ) {
                promises.push(
                    this.pkService.getPKSettings().then((result) => {
                        const pkData: IOtherSettingsListItem = {
                            Id: undefined,
                            Type: OtherSettingsType.Personalkollen,
                            ChangedByUserId: result.ChangedByUserId,
                            ChangedByUserName: result.ChangedByUserName,
                            ChangedOn: result.ChangedOn,
                        };
                        otherSettings.push(pkData);
                    }),
                );
            }

            if (
                this.accessToApplicationTypeService.HasAccessToApplicationType(
                    ApplicationSettingsType.BalooELearning,
                    AccessRightType.Admin,
                )
            ) {
                promises.push(
                    this.balooService.getBalooSettings().then((result) => {
                        const balooItem: IOtherSettingsListItem = {
                            Id: undefined,
                            Type: OtherSettingsType.BalooELearning,
                            ChangedByUserId: result.ChangedByUserId,
                            ChangedByUserName: result.ChangedByUserName,
                            ChangedOn: result.ChangedOn,
                        };
                        otherSettings.push(balooItem);
                    }),
                );
            }

            if (
                this.accessToApplicationTypeService.HasAccessToApplicationType(
                    ApplicationSettingsType.Planday,
                    AccessRightType.Admin,
                )
            ) {
                promises.push(
                    this.pladayService.getPlandaySettings().then((result) => {
                        const plandayItem: IOtherSettingsListItem = {
                            Id: undefined,
                            Type: OtherSettingsType.Planday,
                            ChangedByUserId: result.ChangedByUserId,
                            ChangedByUserName: result.ChangedByUserName,
                            ChangedOn: result.ChangedOn,
                        };
                        otherSettings.push(plandayItem);
                    }),
                );
            }

            if (this.stateAccessCheckService.isStateAllowedForCurrentUser(FiStateNames.Agreement.List)) {
                promises.push(
                    this.agreementService.GetAgreements(new CoreParameterModel()).then((agreements) => {
                        const mappedData: IOtherSettingsListItem[] = agreements.map((agreement) => ({
                            Id: undefined,
                            Name: agreement.Name,
                            ItemId: agreement.Id,
                            Type: OtherSettingsType.Agreements,
                            ChangedByUserId: agreement.ChangedByUserId,
                            ChangedByUserName: agreement.ChangedByUserName,
                            ChangedOn: agreement.ChangedOn,
                        }));
                        otherSettings.push(...mappedData);
                    }),
                );
            }

            Promise.all(promises).then(() => {
                if (
                    this.accessToApplicationTypeService.HasAccessToApplicationType(
                        ApplicationSettingsType.CoreGlobal,
                        AccessRightType.Admin,
                    )
                ) {
                    const transferOwnershipData: IOtherSettingsListItem = {
                        Id: undefined,
                        Type: OtherSettingsType.TransferOwnership,
                    };
                    otherSettings.push(transferOwnershipData);
                }

                if (this.userDataService.isUserSystemObject()) {
                    if (this.stateAccessCheckService.isStateAllowedForCurrentUser(FiStateNames.Import.SystemObject)) {
                        const organizationImportData: IOtherSettingsListItem = {
                            Id: undefined,
                            Type: OtherSettingsType.OrganizationImport,
                        };
                        otherSettings.push(organizationImportData);
                    }
                }

                if (this.stateAccessCheckService.isStateAllowedForCurrentUser(FiStateNames.Import.Handbook.Word)) {
                    const handbookImportData: IOtherSettingsListItem = {
                        Id: undefined,
                        Type: OtherSettingsType.HandbookImport,
                    };
                    otherSettings.push(handbookImportData);
                }

                const items = otherSettings
                    .map((item) => {
                        return <IOtherSettingsListItem>(
                            Object.assign(
                                {},
                                item,
                                this.otherSettingsHelperService.getOtherSettingsItemNameAndDescriptionByType(item.Type),
                            )
                        );
                    })
                    .sort(comparator((item) => item.Name));
                resolve({
                    Items: items,
                    TotalCount: otherSettings.length,
                    LightWeightObjectsForLocalSearch: undefined,
                });
            }, reject);
        });
    }
}
